import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './AppShell.js';
import { AuthProvider } from './auth/AuthProvider.js';
import { RequireAuth } from './auth/RequireAuth.js';
import { LoginPage } from './pages/LoginPage.js';
import { SignupPage } from './pages/SignupPage.js';
import { JoinPage } from './pages/JoinPage.js';
import { PrivacyPage } from './pages/PrivacyPage.js';
import { SyncStatusPage } from './pages/SyncStatusPage.js';
import { EditorPage } from './pages/EditorPage.js';
import { ItemsPage } from './pages/ItemsPage.js';
import { CountStockPage } from './pages/CountStockPage.js';
import { ItemHistoryPage } from './pages/ItemHistoryPage.js';
import { AccountsPage } from './pages/AccountsPage.js';
import { PartiesPage } from './pages/PartiesPage.js';
import { TriagePage } from './pages/TriagePage.js';
import { ClearingsPage } from './pages/ClearingsPage.js';
import { BillsPage } from './pages/BillsPage.js';
import { InvoicesPage } from './pages/InvoicesPage.js';
import { CashBasisPage } from './pages/CashBasisPage.js';
import { ShareholderPage } from './pages/ShareholderPage.js';
import { OwnersEquityPage } from './pages/OwnersEquityPage.js';
import { PartnersCapitalPage } from './pages/PartnersCapitalPage.js';
import { CashflowPage } from './pages/CashflowPage.js';
import { HomePage } from './pages/HomePage.js';
import { CommitmentsPage } from './pages/CommitmentsPage.js';
import { ReportsPage } from './pages/ReportsPage.js';
import { AccountLinesPage } from './pages/AccountLinesPage.js';
import { RulesPage } from './pages/RulesPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { ReconciliationPage } from './pages/ReconciliationPage.js';
import { PayrollPage } from './pages/PayrollPage.js';
import { DepositsPage } from './pages/DepositsPage.js';
import { RegisterPage } from './pages/RegisterPage.js';
import { YearToDatePage } from './pages/YearToDatePage.js';
import { W2Page } from './pages/W2Page.js';
import { Form1099NecPage } from './pages/Form1099NecPage.js';
import { Form940Page } from './pages/Form940Page.js';
import { Form941Page } from './pages/Form941Page.js';
import { TwcPage } from './pages/TwcPage.js';
import { EmployeePage } from './pages/EmployeePage.js';
import { NewPayRunPage } from './pages/NewPayRunPage.js';
import { PayRunPage } from './pages/PayRunPage.js';
import { DonorLetterPage } from './pages/DonorLetterPage.js';
import { EarningsStatementPage } from './pages/EarningsStatementPage.js';

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Public, like /login: somebody creating a business has no session. */}
          <Route path="/signup" element={<SignupPage />} />
          {/* Public too: a person joining from an invitation has no session in that business yet. */}
          <Route path="/join" element={<JoinPage />} />
          {/* Public too: read before signing up, and linked from Plaid's form (issue #56). */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route
            path="/sync"
            element={
              <RequireAuth>
                <AppShell>
                  <SyncStatusPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/triage"
            element={
              <RequireAuth>
                <AppShell>
                  <TriagePage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/*
            `/journals/new` before `/journals/:id` is not required by the
            router -- `:id` would match "new" and the editor branches on it --
            but a single entry-point keeps the editor as the only screen that
            knows how an entry is created.
          */}
          <Route
            path="/journals/:id"
            element={
              <RequireAuth>
                <AppShell>
                  <EditorPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/invoices"
            element={
              <RequireAuth>
                <AppShell>
                  <InvoicesPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/bills"
            element={
              <RequireAuth>
                <AppShell>
                  <BillsPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/clearings"
            element={
              <RequireAuth>
                <AppShell>
                  <ClearingsPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/reports"
            element={
              <RequireAuth>
                <AppShell>
                  <ReportsPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* What is coming, on one timeline. Its own route rather than a
              fourth tab inside Reports: the statements look backwards over a
              chosen period and this looks forwards over a horizon, and one
              From/To control cannot mean both. */}
          <Route
            path="/reports/cashflow"
            element={
              <RequireAuth>
                <AppShell>
                  <CashflowPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* §9.9 — the drill-down behind a report figure. */}
          <Route
            path="/reports/account"
            element={
              <RequireAuth>
                <AppShell>
                  <AccountLinesPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/reports/shareholder"
            element={
              <RequireAuth>
                <AppShell>
                  <ShareholderPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/reports/owners-equity"
            element={
              <RequireAuth>
                <AppShell>
                  <OwnersEquityPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/reports/partners-capital"
            element={
              <RequireAuth>
                <AppShell>
                  <PartnersCapitalPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/reports/cash-basis"
            element={
              <RequireAuth>
                <AppShell>
                  <CashBasisPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* §6.10 — what leaves on a schedule, and what did not arrive. */}
          <Route
            path="/commitments"
            element={
              <RequireAuth>
                <AppShell>
                  <CommitmentsPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/rules"
            element={
              <RequireAuth>
                <AppShell>
                  <RulesPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/parties"
            element={
              <RequireAuth>
                <AppShell>
                  <PartiesPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/items"
            element={
              <RequireAuth>
                <AppShell>
                  <ItemsPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* 0088 (I2b): a count, and an item's history. */}
          <Route
            path="/items/count"
            element={
              <RequireAuth>
                <AppShell>
                  <CountStockPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/items/:id"
            element={
              <RequireAuth>
                <AppShell>
                  <ItemHistoryPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/accounts"
            element={
              <RequireAuth>
                <AppShell>
                  <AccountsPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/reconciliation"
            element={
              <RequireAuth>
                <AppShell>
                  <ReconciliationPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* Spec §18 — payroll: employees, their W-4s and pay; stage 3's runs. */}
          <Route
            path="/payroll"
            element={
              <RequireAuth>
                <AppShell>
                  <PayrollPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/payroll/deposits"
            element={
              <RequireAuth>
                <AppShell>
                  <DepositsPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/payroll/register"
            element={
              <RequireAuth>
                <AppShell>
                  <RegisterPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/payroll/year-to-date"
            element={
              <RequireAuth>
                <AppShell>
                  <YearToDatePage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/payroll/forms/941"
            element={
              <RequireAuth>
                <AppShell>
                  <Form941Page />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/payroll/forms/1099-nec"
            element={
              <RequireAuth>
                <AppShell>
                  <Form1099NecPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/payroll/forms/w2"
            element={
              <RequireAuth>
                <AppShell>
                  <W2Page />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/payroll/forms/940"
            element={
              <RequireAuth>
                <AppShell>
                  <Form940Page />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/payroll/forms/twc"
            element={
              <RequireAuth>
                <AppShell>
                  <TwcPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/payroll/employees/:id"
            element={
              <RequireAuth>
                <AppShell>
                  <EmployeePage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/payroll/runs/new"
            element={
              <RequireAuth>
                <AppShell>
                  <NewPayRunPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/payroll/runs/:id"
            element={
              <RequireAuth>
                <AppShell>
                  <PayRunPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/donors/:partyId/letter"
            element={
              <RequireAuth>
                <AppShell>
                  <DonorLetterPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/payroll/runs/:id/statement"
            element={
              <RequireAuth>
                <AppShell>
                  <EarningsStatementPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* Reached from the profile menu, not the nav. See SettingsPage. */}
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <AppShell>
                  <SettingsPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* The draft queue is the landing screen: the uncoded backlog is the
              standing job, and /sync is a status page you visit deliberately. */}
          {/*
            `/` was a redirect to the queue, which opened on the WORK rather
            than on the position. The dashboard opens on what is coming, and
            says what that rests on: the forecast is only as accurate as the
            queue is worked, so queue health sits above the money.
          */}
          <Route
            path="/"
            element={
              <RequireAuth>
                <AppShell>
                  <HomePage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
